/**
 * LLM Provider Mode Selector + insufficient-credit switch guard.
 * ---------------------------------------------------------------------------
 * <LlmModeSelector />        — segmented Commercial / Open-Source toggle the
 *                              end-user picks as the active LLM. Shows the
 *                              remaining commercial AI credits.
 * useLlmCreditGuard()        — returns { guardDialog, ensureSufficientCredit }.
 *                              Call ensureSufficientCredit() before any AI
 *                              action; if the active mode lacks credit it
 *                              prompts the user to switch to the free OSS mode
 *                              and resolves true (proceed) / false (abort).
 */

import React, { useCallback, useEffect, useState } from 'react'
import { Cpu, Leaf, Coins, Loader2, Wand2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { useQueryClient } from '@tanstack/react-query'
import {
  useProviderMode,
  useSetProviderMode,
  useLlmModeStore,
  llmModeKeys,
  fetchProviderStatus,
  setProviderMode,
  type LlmMode,
  type ProviderModeStatus,
} from '@/services/llm-mode.service'

// ── Selector ──────────────────────────────────────────────────────────────

export function LlmModeSelector({ className }: { className?: string }) {
  const { data: status, isLoading } = useProviderMode()
  const setModeMut = useSetProviderMode()
  const setStoreStatus = useLlmModeStore((s) => s.setStatus)

  useEffect(() => {
    if (status) setStoreStatus(status)
  }, [status, setStoreStatus])

  const mode: LlmMode = status?.mode ?? 'commercial'
  const credits = status?.commercial_credits ?? 0
  const ossModel = status?.oss_model ?? 'qwen3-14b'
  const gpuEnabled = status?.gpu_enabled !== false

  const pick = (next: LlmMode) => {
    if (next === mode || setModeMut.isPending) return
    if (next === 'oss' && !gpuEnabled) return // OSS disabled while GPU is off
    setModeMut.mutate({ mode: next })
  }

  const segBtn = (
    value: LlmMode,
    label: string,
    Icon: typeof Cpu,
    activeClass: string,
    title: string,
    disabled = false,
  ) => (
    <button
      type="button"
      onClick={() => pick(value)}
      aria-pressed={mode === value}
      disabled={disabled}
      data-testid={`llm-mode-${value}`}
      data-disabled={disabled ? 'true' : undefined}
      className={cn(
        'flex items-center gap-1 rounded px-2 py-1 transition-colors',
        disabled && 'opacity-40 cursor-not-allowed',
        mode === value
          ? `bg-background font-medium shadow-sm ${activeClass}`
          : 'text-muted-foreground hover:text-foreground',
      )}
      title={title}
    >
      <Icon className="h-3.5 w-3.5" />
      {label}
    </button>
  )

  return (
    <div
      className={cn('flex items-center gap-2', className)}
      data-testid="llm-mode-selector"
      data-mode={mode}
    >
      <div className="inline-flex rounded-md border border-border bg-muted/40 p-0.5 text-xs">
        {segBtn('commercial', 'Commercial', Cpu, 'text-foreground',
          'LLM commercial (Claude / GPT) — consomme des crédits IA')}
        {segBtn('auto', 'Auto', Wand2, 'text-indigo-600',
          gpuEnabled
            ? 'Auto — commercial tant qu’il y a du crédit, bascule OSS gratuit sinon'
            : 'Auto — commercial (GPU désactivé : pas de bascule OSS)')}
        {segBtn('oss', 'OSS', Leaf, 'text-emerald-600',
          gpuEnabled
            ? `LLM open source (${ossModel}) — gratuit, 0 crédit`
            : 'Mode OSS indisponible — GPU désactivé par l’administrateur',
          !gpuEnabled)}
      </div>
      {(isLoading || setModeMut.isPending) ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
      ) : mode === 'oss' ? (
        <span className="text-xs text-emerald-600" title={`Mode gratuit — ${ossModel}`}>gratuit</span>
      ) : (
        <span
          className={cn(
            'inline-flex items-center gap-1 text-xs',
            credits <= 0 ? 'text-destructive' : 'text-muted-foreground',
          )}
          title="Crédits IA commerciaux restants"
        >
          <Coins className="h-3.5 w-3.5" />
          {credits}
        </span>
      )}
    </div>
  )
}

// ── Pre-action credit guard ─────────────────────────────────────────────────

interface PendingPrompt {
  status: ProviderModeStatus
  resolve: (proceed: boolean) => void
}

export function useLlmCreditGuard() {
  const [pending, setPending] = useState<PendingPrompt | null>(null)
  const [switching, setSwitching] = useState(false)
  const setStoreStatus = useLlmModeStore((s) => s.setStatus)
  const qc = useQueryClient()

  /**
   * Resolve true if the active LLM mode can run an AI action; otherwise prompt
   * the user to switch to the free OSS mode and resolve their choice.
   */
  const ensureSufficientCredit = useCallback(
    async (estimated = 5): Promise<boolean> => {
      const status = await fetchProviderStatus(estimated)
      setStoreStatus(status)
      if (status.sufficient) return true
      return new Promise<boolean>((resolve) => setPending({ status, resolve }))
    },
    [setStoreStatus],
  )

  const onSwitch = useCallback(async () => {
    if (!pending) return
    const target: LlmMode = pending.status.suggested_mode ?? 'oss'
    setSwitching(true)
    try {
      const s = await setProviderMode(target, pending.status.estimated_needed)
      setStoreStatus(s)
      // Refresh the selector's query cache so its badge flips to OSS immediately.
      qc.invalidateQueries({ queryKey: llmModeKeys.all })
    } catch {
      /* even if persistence fails, let the action proceed in OSS */
    } finally {
      setSwitching(false)
      pending.resolve(true)
      setPending(null)
    }
  }, [pending, setStoreStatus, qc])

  const onCancel = useCallback(() => {
    pending?.resolve(false)
    setPending(null)
  }, [pending])

  const guardDialog = (
    <AlertDialog open={!!pending} onOpenChange={(o) => { if (!o) onCancel() }}>
      <AlertDialogContent data-testid="llm-credit-switch-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Crédit IA insuffisant</AlertDialogTitle>
          <AlertDialogDescription>
            Le mode commercial (Claude / GPT) ne dispose plus d&apos;assez de crédits IA
            {' '}({pending?.status.commercial_credits ?? 0} restant
            {(pending?.status.commercial_credits ?? 0) > 1 ? 's' : ''}) pour cette action.
            Voulez-vous basculer sur le mode open source (Mistral / Llama, gratuit) afin de
            continuer&nbsp;?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onCancel} disabled={switching}>
            Rester en commercial
          </AlertDialogCancel>
          <AlertDialogAction onClick={onSwitch} disabled={switching}>
            {switching ? (
              <Loader2 className="mr-1 h-4 w-4 animate-spin" />
            ) : (
              <Leaf className="mr-1 h-4 w-4" />
            )}
            Basculer en open source
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )

  return { guardDialog, ensureSufficientCredit }
}
